import { memo } from "react"
import styled from "styled-components";
import { ReactComponent as Geocloud } from '~/assets/svg/geocloud_logo.svg';
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Routes from "~/routes/routes";
import Avatar from "../components/Header/components/Avatar";
import { CloseOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { useIsTLGeoManager } from "~/screens/FreePhotoScreen/hooks/useIsTLGeoManager";
import {
  PiMapTrifoldBold,
  PiCameraBold,
  PiBuildingsBold,
  PiDeviceMobileCameraBold,
  PiNotePencilBold,
  PiClipboardTextBold,
} from "react-icons/pi";

interface SidebarProps {
  onMobileClose?: () => void;
  className?: string;
}

const Sidebar = ({ onMobileClose, className }: SidebarProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isTLGeoManager = useIsTLGeoManager();

  const handleNavigation = (path: string) => {
    navigate(path);
    if (onMobileClose) {
      onMobileClose();
    }
  };

  const isProjectActive = location.pathname === Routes.PROJECT || location.pathname.startsWith(`${Routes.PROJECT}/`);
  const isFreePhotosActive = location.pathname === Routes.FREE_PHOTOS || location.pathname.startsWith(`${Routes.FREE_PHOTOS}/`);
  const isOrgActive = location.pathname === Routes.ORGANIZATION || location.pathname.startsWith(`${Routes.ORGANIZATION}/`);
  const isTransferActive = location.pathname === Routes.TRANSFER_TO_MOBILE || location.pathname.startsWith(`${Routes.TRANSFER_TO_MOBILE}/`);
  const isCollectFormActive = location.pathname === Routes.COLLECT_FORMS;
  const isCollectFormTemplateActive =
    location.pathname === Routes.COLLECT_FORM_TEMPLATES ||
    location.pathname.startsWith(`${Routes.COLLECT_FORM_TEMPLATES}/`) ||
    location.pathname.startsWith('/collect-form/design') ||
    location.pathname === Routes.FORM_BUILDER ||
    location.pathname.startsWith(`${Routes.FORM_BUILDER}/`);

  return <UI_Wrapper className={className}>
    <UI_Header>
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <Geocloud style={{ width: 50, height: 50 }} />
        <span> Geocloud </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Avatar />
        <UI_MobileCloseButton
          onClick={onMobileClose}
          icon={<CloseOutlined />}
          type="text"
          size="small"
        />
      </div>
    </UI_Header>

    <UI_MenuList>
      <ul>
        <UI_MenuItem $active={isProjectActive} onClick={() => handleNavigation(Routes.PROJECT)}>
          <UI_MenuIcon $active={isProjectActive}>
            <PiMapTrifoldBold />
          </UI_MenuIcon>
          <span>{t('sidebar_projects')}</span>
        </UI_MenuItem>
        
        <UI_MenuItem $active={isOrgActive} onClick={() => handleNavigation(Routes.ORGANIZATION)}>
          <UI_MenuIcon $active={isOrgActive}>
            <PiBuildingsBold />
          </UI_MenuIcon>
          <span>{t('sidebar_organizations')}</span>
        </UI_MenuItem>

        <UI_MenuItem $active={isCollectFormActive} onClick={() => handleNavigation(Routes.COLLECT_FORMS)}>
          <UI_MenuIcon $active={isCollectFormActive}>
            <PiClipboardTextBold />
          </UI_MenuIcon>
          <span>{t('sidebar_forms')}</span>
        </UI_MenuItem>

        <UI_SectionDivider>
          {t('sidebar_utilities')}
        </UI_SectionDivider>

        <UI_MenuItem $active={isTransferActive} onClick={() => handleNavigation(Routes.TRANSFER_TO_MOBILE)}>
          <UI_MenuIcon $active={isTransferActive}>
            <PiDeviceMobileCameraBold />
          </UI_MenuIcon>
          <span>{t('sidebar_send_to_geocollect')}</span>
        </UI_MenuItem>

        {isTLGeoManager && (
          <>
            <UI_SectionDivider>
              {t('sidebar_admin_only')}
            </UI_SectionDivider>

            <UI_MenuItem $active={isCollectFormTemplateActive} onClick={() => handleNavigation(Routes.COLLECT_FORM_TEMPLATES)}>
              <UI_MenuIcon $active={isCollectFormTemplateActive}>
                <PiNotePencilBold />
              </UI_MenuIcon>
              <span>{t('sidebar_form_templates')}</span>
            </UI_MenuItem>

            <UI_MenuItem $active={isFreePhotosActive} onClick={() => handleNavigation(Routes.FREE_PHOTOS)}>
              <UI_MenuIcon $active={isFreePhotosActive}>
                <PiCameraBold />
              </UI_MenuIcon>
              <span>{t('sidebar_free_photos')}</span>
            </UI_MenuItem>
          </>
        )}
      </ul>
    </UI_MenuList>
  </UI_Wrapper>
}
export default memo(Sidebar);

const UI_Wrapper = styled.div`
  width: 250px;
  height: 100%;
  border-right: 1px solid #eaeaea;
  background-color: #fff;
  display: flex;
  flex-direction: column;

  @media (max-width: 1024px) {
    width: 200px;
  }

  @media (max-width: 768px) {
    width: 280px;
    height: 100vh;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 2000;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  }

  &.mobile-open {
    @media (max-width: 768px) {
      transform: translateX(0);
    }
  }

  @media (max-width: 480px) {
    width: 100vw;
  }
`

const UI_MenuList = styled.div`
  padding: 8px 0;
  flex: 1;
  overflow-y: auto;

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
`;

const UI_MenuItem = styled.li<{ $active?: boolean }>`
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 2px 8px;
  border-radius: 8px;
  user-select: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  background-color: ${({ $active }) => ($active ? 'rgba(59, 130, 246, 0.1)' : 'transparent')};
  color: ${({ $active }) => ($active ? '#2563eb' : '#475569')};
  font-weight: ${({ $active }) => ($active ? '600' : '450')};

  &:hover {
    background-color: ${({ $active }) => ($active ? 'rgba(59, 130, 246, 0.16)' : '#f1f5f9')};
    color: ${({ $active }) => ($active ? '#1d4ed8' : '#0f172a')};
  }

  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 15px;
    min-height: 44px;
  }

  @media (max-width: 480px) {
    padding: 12px 16px;
    font-size: 16px;
    min-height: 48px;
  }
`;

const UI_MenuIcon = styled.div<{ $active?: boolean }>`
  width: 28px;
  height: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
  font-size: 18px;
  color: ${({ $active }) => ($active ? '#2563eb' : '#64748b')};
  transition: color 0.2s ease;
`;

const UI_SectionDivider = styled.div`
  padding: 14px 16px 4px 16px;
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 4px;
`;

const UI_Header = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 12px 16px;
    justify-content: space-between;
    border-bottom: 1px solid #eaeaea;
  }
`

const UI_MobileCloseButton = styled(Button)`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 32px;
  }
`